import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import type { DmpAdapter } from "parog-web/cnam-adapters/dmp-adapter";
import type { InsiAdapter } from "parog-web/cnam-adapters/insi-adapter";
import type { InsIdentityResult, InsOid } from "parog-web/cnam/types";
import { InsIdentityResultCode, InsStatus } from "parog-web/cnam/types";
import {
  CnamApiCpsNoBillingNumber,
  CnamApiInsConflictWithSsnError,
  CnamApiInsIdentityCorruptedError,
  CnamApiInsMultipleIdentityFoundError,
  CnamApiInsNoIdentityFoundError,
  CnamApiInsNoSexError,
  CnamApiInsPatientInvalid
} from "parog-web/utils/cnam-api-errors";

import type Patient from "parog-web/models/patient";
import InsRetrieveAccessFormSetPracticeLocationWithBillingNumberComponent from "parog-web/components/ins-retrieve-access-form/set-practice-location-with-billing-number";
import searchBirthPlace from "parog-web/utils/search-birthplace";
import type { OperationType } from "./abstract/cnam-context";
import CnamContext, { State } from "./abstract/cnam-context";
import type { AuthenticationContext } from "./authentication-context";

export default class InsRetrievalContext extends CnamContext {
  declare authenticationContext: AuthenticationContext;
  declare insiAdapter: InsiAdapter;
  declare dmpAdapter: DmpAdapter;
  declare patient: Patient;
  declare onSuccess: (() => Promise<void>) | undefined;

  @tracked checkedBillingNumber = false;
  @tracked validatedPatient = false;
  @tracked declare practiceLocation: string;

  @tracked insProcessNeeded = true;

  @tracked declare identityResult: InsIdentityResult;

  constructor(
    cnamAdapterName: string,
    patient: Patient,
    authenticationContext: AuthenticationContext,
    insiAdapter: InsiAdapter,
    dmpAdapter: DmpAdapter,
    store: Store,
    requestManager,
    operationType: OperationType,
    onSuccess?: () => Promise<void>
  ) {
    super(store, requestManager, cnamAdapterName, patient, operationType);
    this.authenticationContext = authenticationContext;
    this.insiAdapter = insiAdapter;
    this.dmpAdapter = dmpAdapter;
    this.onSuccess = onSuccess;

    this.authenticationContext.forcedIdentifier =
      this.authenticationContext.medic.forceIdentifierForInsRetrieval;

    this.states = [
      ...this.states,
      new State(
        "validate-patient",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.validatedPatient &&
              this.patient.insStatus != InsStatus.Qualified &&
              this.insProcessNeeded
          );
        },
        async (): Promise<void> => {
          const missingMandatoryFields: string[] = [];

          if (!this.patient.birthday) {
            missingMandatoryFields.push("date de naissance");
          }

          if (!this.patient.gender) {
            missingMandatoryFields.push("sexe");
          }

          if (!this.patient.birthName && !this.patient.lastName) {
            missingMandatoryFields.push("nom de naissance");
          }

          if (!this.patient.birthFirstName && !this.patient.firstName) {
            missingMandatoryFields.push("prénom(s) de naissance");
          }

          if (missingMandatoryFields.length > 0) {
            throw new CnamApiInsPatientInvalid(
              `Il manque les informations suivantes dans la fiche patient : ${missingMandatoryFields.join(
                ", "
              )}.`
            );
          } else {
            this.validatedPatient = true;
          }
        }
      ),
      new State(
        "check-valid-billing-number",
        (): Promise<boolean> => {
          return Promise.resolve(
            !(
              this.authenticationContext.billingNumber ||
              this.checkedBillingNumber
            ) &&
              this.patient.insStatus != InsStatus.Qualified &&
              this.insProcessNeeded
          );
        },
        async (): Promise<void> => {
          if (
            this.authenticationContext.practiceLocationsWithValidBillingNumber
              .length == 0
          ) {
            throw new CnamApiCpsNoBillingNumber();
          }
          this.checkedBillingNumber = true;
        }
      ),
      new State(
        "select-another-practice-location-with-billing-number",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.authenticationContext.billingNumber &&
              this.checkedBillingNumber &&
              this.authenticationContext.practiceLocationsWithValidBillingNumber
                .length > 0 &&
              this.patient.insStatus != InsStatus.Qualified &&
              this.insProcessNeeded
          );
        },
        async (): Promise<void> => {
          this.authenticationContext.practiceLocation = this.practiceLocation;
          await this.authenticationContext.updatePracticeLocationNameAndIndice();
        },
        InsRetrieveAccessFormSetPracticeLocationWithBillingNumberComponent
      ),
      new State(
        "retrieve-ins",
        (): Promise<boolean> => {
          return Promise.resolve(
            this.patient.insStatus != InsStatus.Qualified &&
              this.insProcessNeeded
          );
        },
        async (): Promise<void> => {
          try {
            const identity = await this.insiAdapter.retrieveInsIdentity(
              this.patient
            );

            this._checkIdentityResponse(identity, patient.socialSecurityNumber);

            this.identityResult = identity;

            this.insProcessNeeded = false;
          } catch (error) {
            const identity = error.identity;
            if (identity) {
              await this._saveInsRetrieve(patient, identity, false);
            }
            throw error;
          }
        }
      )
    ];
  }

  _checkIdentityResponse(
    identity: InsIdentityResult,
    currentSsn?: string
  ): void {
    if (
      identity.result ==
        InsIdentityResultCode.InsIdentityResult_NoIdentityFound &&
      !identity.identity
    ) {
      throw new CnamApiInsNoIdentityFoundError("", undefined, identity);
    } else if (
      identity.result ==
        InsIdentityResultCode.InsIdentityResult_MultipleIdentitiesFound &&
      !identity.identity
    ) {
      throw new CnamApiInsMultipleIdentityFoundError("", undefined, identity);
    } else if (
      !(
        identity.birthGiven &&
        identity.birthName &&
        identity.birthday &&
        identity.sex
      )
    ) {
      throw new CnamApiInsIdentityCorruptedError("", undefined, identity);
    } else if (
      currentSsn &&
      this._removeSuffix(currentSsn) !== this._computeSsn(identity)
    ) {
      throw new CnamApiInsConflictWithSsnError("", undefined, identity);
    } else if (!identity.sex) {
      throw new CnamApiInsNoSexError("", undefined, identity);
    }
  }

  _computeSsn(identity?: InsIdentityResult): string {
    if (!identity || !identity.identity) return "";
    return `${identity.identity.value}${identity.identity.key}`;
  }

  _removeSuffix(nir: string): string {
    return nir.replace(/D|T|A/, "");
  }

  async _savePatientIdentity(
    identity: InsIdentityResult,
    patient: Patient
  ): Promise<void> {
    if (
      identity.result ==
        InsIdentityResultCode.InsIdentityResult_SingleIdentityFound &&
      identity.identity
    ) {
      patient.socialSecurityNumber = `${identity.identity?.value}${identity.identity?.key}`;
      patient.insOid = identity.identity.oid as InsOid;
      patient.insKey = identity.identity.key;
      patient.firstBirthFirstName ??= identity.birthGiven
        ? identity.birthGiven?.split(" ")[0]
        : "";
      patient.birthFirstName = identity.birthGiven as string;
      patient.birthName = identity.birthName as string;
      patient.birthday = identity.birthday as Date;
      patient.birthMunicipalityCode = identity.birthPlace;
      if (identity.birthPlace) {
        patient.birthPlace = await searchBirthPlace(identity.birthPlace);
      }

      patient.gender = identity.sex as "male" | "female";
      patient.insValue = identity.identity.value;
      await patient.save();
    }
  }

  async _saveInsRetrieve(
    patient: Patient,
    identity: InsIdentityResult,
    succeed: boolean
  ): Promise<void> {
    await this.store
      .createRecord("ins-retrieved-journal-event", {
        patient,
        occurredAt: new Date(),
        identity,
        succeed,
        cnamProcessStartedJournalEvent: this.cnamProcessStartedJournalEvent
      })
      .save();

    // insStatus of the patient is computed server-side when an "ins-retrieved-journal-event" is pushed.
    // Let's retrieve it.
    await patient.reload();
  }

  reset() {
    super.reset();
    this.validatedPatient = false;
  }
}
